import {
  BalanceAccountAmountComponent,
  BondPriceComponent,
  HoldingValueComponent,
  YieldComponent,
} from "@goodfill/domain-properties-components";
import { Currency } from "@goodfill/models-typescript";
import { sortByKeys, SortColumn } from "@goodfill/sort";
import { Button } from "@goodfill/ui-components";
import { useMemo, useState } from "react";
import DataGrid, { Column } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { route } from "../../../../../routes";
import { GridTableFilterRow } from "../../../../../ui-components/GridTable/GridTableFilterRow";
import { InfoTooltip } from "../../../../../ui-components/tooltip/InfoTooltip";
import { getPortfolioHoldingsComparator } from "./PortfolioHoldingsComparator";
import { PresentedAccountBondHolding } from "./PresentedAccountBondHolding";
import AutoSizer from "react-virtualized-auto-sizer";
import { mapSortColumnFromGrid } from "../../../../../sort/mapSortColumnFromGrid";
import {
  defaultPortfolioHoldingsFilterConstraints,
  PortfolioHoldingsFilter,
  portfolioHoldingsFilters,
} from "./PortfolioHoldingsFilter";
import { asAmount, asBondPriceNominal, asYield } from "@goodfill/domain-properties";
import { MaturityDateColumnDefinition } from "../../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import { applyFilters } from "../../../../../filters/filters";
import { DecimalRangeFilter } from "../../../../../filters/DecimalRangeFilter";
import { IsinColumnDefinition, getSortedIsins } from "../../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  IssuerIdColumnDefinition,
  getSortedIssuerIds,
} from "../../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import { MoodysRatingColumnDefinition } from "../../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  getSortedPaymentRanks,
  PaymentRankColumnDefinition,
} from "../../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import { SpRatingColumnDefinition } from "../../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import {
  TickerColumnDefinition,
  getSortedTickers,
} from "../../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import {
  CouponPaymentColumnDefinition,
  getSortedBondCouponPayments,
} from "../../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";

type SortableKeys = keyof PresentedAccountBondHolding;

export const PortfolioHoldingsTable = ({
  bondHoldings,
  currency,
}: { bondHoldings: PresentedAccountBondHolding[]; currency: Currency }) => {
  const navigate = useNavigate();
  const [sortColumns, setSortColumns] = useState<readonly SortColumn<SortableKeys>[]>([]);

  const [criteria, setCriteria] = useState(defaultPortfolioHoldingsFilterConstraints);

  const onFilterChange = <T extends keyof PortfolioHoldingsFilter>(key: T, value: PortfolioHoldingsFilter[T]) =>
    setCriteria({ ...criteria, [key]: value });

  const filteredRows = applyFilters(bondHoldings, portfolioHoldingsFilters, criteria);

  const tableDefinition: Array<
    Column<PresentedAccountBondHolding> & {
      key: keyof PresentedAccountBondHolding | "_positions";
    }
  > = useMemo(() => {
    const issuerIds = getSortedIssuerIds("issuerId", bondHoldings);
    const isins = getSortedIsins("bondId", bondHoldings);
    const tickers = getSortedTickers("ticker", bondHoldings);
    const paymentRanks = getSortedPaymentRanks("paymentRank", bondHoldings);
    const couponPayments = getSortedBondCouponPayments("couponPayment", bondHoldings);

    return [
      {
        key: "_positions",
        name: "",
        headerCellClass: "GridTableFilterRow__header-cell",
        formatter: ({ row }) => (
          <Button
            fill
            size="small"
            primary={true}
            data-testid={`positions-${row.bondId}`}
            onClick={() => navigate(route.positions(row.bondId))}
          >
            Positions
          </Button>
        ),
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
        sortable: false,
      },
      TickerColumnDefinition("ticker", criteria, onFilterChange, tickers),
      CouponPaymentColumnDefinition("couponPayment", criteria, onFilterChange, couponPayments),
      MaturityDateColumnDefinition("maturityDate", criteria, onFilterChange),
      IssuerIdColumnDefinition("issuerId", criteria, onFilterChange, issuerIds),
      PaymentRankColumnDefinition("paymentRank", criteria, onFilterChange, paymentRanks),
      {
        key: "balance",
        name: (
          <>
            Face Amount
            <InfoTooltip text="Includes Open Orders and Available to Sell." />
          </>
        ),
        headerCellClass: "GridTableFilterRow__header-cell",
        formatter: ({ row }) => <BalanceAccountAmountComponent value={row.balance} currency={currency} />,
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={criteria.balanceRange}
              onChange={(value) =>
                onFilterChange("balanceRange", [
                  value[0] !== null ? asAmount(value[0]) : null,
                  value[1] !== null ? asAmount(value[1]) : null,
                ])
              }
              leftIcon="$"
            />
          </GridTableFilterRow>
        ),
        width: 220,
        sortable: true,
      },
      {
        key: "baseBondBidPrice",
        name: <>Bid Last Price</>,
        headerCellClass: "GridTableFilterRow__header-cell",
        formatter: ({ row }) => <BondPriceComponent value={row.baseBondBidPrice} />,
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={[
                criteria.baseBondBidPriceRange[0] !== null ? criteria.baseBondBidPriceRange[0].mul(100) : null,
                criteria.baseBondBidPriceRange[1] !== null ? criteria.baseBondBidPriceRange[1].mul(100) : null,
              ]}
              onChange={(value) =>
                onFilterChange("baseBondBidPriceRange", [
                  value[0] !== null ? asBondPriceNominal(value[0].div(100)) : null,
                  value[1] !== null ? asBondPriceNominal(value[1].div(100)) : null,
                ])
              }
            />
          </GridTableFilterRow>
        ),
        width: 170,
        sortable: true,
      },
      {
        key: "bidBondValue",
        name: <>Value (Bid Price × Face)</>,
        headerCellClass: "GridTableFilterRow__header-cell",
        formatter: ({ row }) => <HoldingValueComponent value={row.bidBondValue} currency={currency} />,
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={criteria.bidBondValueRange}
              onChange={(value) =>
                onFilterChange("bidBondValueRange", [
                  value[0] !== null ? asAmount(value[0]) : null,
                  value[1] !== null ? asAmount(value[1]) : null,
                ])
              }
              leftIcon="$"
            />
          </GridTableFilterRow>
        ),
        width: 190,
        sortable: true,
      },
      {
        key: "baseBondBidYield",
        name: "Yield",
        headerCellClass: "GridTableFilterRow__header-cell",
        formatter: ({ row }) => <YieldComponent value={row.baseBondBidYield} />,
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              leftIcon="%"
              value={[
                criteria.baseBondBidYieldRange[0] !== null ? criteria.baseBondBidYieldRange[0].mul(100) : null,
                criteria.baseBondBidYieldRange[1] !== null ? criteria.baseBondBidYieldRange[1].mul(100) : null,
              ]}
              onChange={(value) =>
                onFilterChange("baseBondBidYieldRange", [
                  value[0] !== null ? asYield(value[0].div(100)) : null,
                  value[1] !== null ? asYield(value[1].div(100)) : null,
                ])
              }
            />
          </GridTableFilterRow>
        ),
        width: 190,
        sortable: true,
      },
      MoodysRatingColumnDefinition("bondRatingMoodys", criteria, onFilterChange),
      SpRatingColumnDefinition("bondRatingSp", criteria, onFilterChange),
      IsinColumnDefinition("bondId", criteria, onFilterChange, isins),
    ];
  }, [navigate, currency, bondHoldings, onFilterChange, criteria]);

  // TODO: abstract sortedRows/summaryRows into useSortedRows(rows) and useSummaryRows(rows), respectively
  const sortedRows = useMemo(
    () => sortByKeys(filteredRows, sortColumns, getPortfolioHoldingsComparator),
    [filteredRows, sortColumns],
  );

  const isDark = false;

  return (
    <AutoSizer disableHeight>
      {({ height, width }) => (
        <DataGrid
          className={`rdg-copyable ${isDark ? "rdg-dark" : "rdg-light"} OrderListTable custom-scrollbar`}
          columns={tableDefinition}
          rows={sortedRows}
          headerRowHeight={75}
          sortColumns={sortColumns}
          onSortColumnsChange={(columns) => setSortColumns(columns.map(mapSortColumnFromGrid<SortableKeys>))}
          style={{ height: 700, width }}
        />
      )}
    </AutoSizer>
  );
};
